import { memo, useCallback, useEffect, useState } from 'react';
import AgProfilePage from '../../stories/Components/PageTemplates/AgProfilePage';
import { AgTabsDataProps } from '../../stories/Components/AgTab';
import defaultAvatar from '../../stories/Components/AgAvatar/defaultAvatar.svg';
import AgTypography from '../../stories/Styles/AgTypography';
import AgInfoCard from '../../stories/Components/Cards/AgInfoCard';
import { useIntl } from 'react-intl';
import PersonalDetails from './PersonalDetails';
import Settings from './Settings';
import ProfileDetails from './ProfileDetails';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ACCOUNT_TYPE_ID,
  CustomFieldColumnType,
  CustomFieldItemType,
  MEMBER_EDIT_PERMISSION,
  MEMBER_READ_PERMISSION, SUCCESS_STATUS
} from '../../Constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getCustomFieldByTagAndFunction,
  getEnumList,
  getMemberProfile,
  updateIdentity,
  updateUser
} from '../Users/redux';
import {
  ApiCustomField, ApiEnumGroup, ApiIdentity,
  ApiIdentityOut, ApiItemData, ApiOrganizationProfile,
  ApiUserInfo
} from '@agunity/api-v4/build/data-contracts';
import AgButton from '../../stories/Components/AgButton';
import AgModal from '../../stories/AgModal';
import {
  selectIsOpenViewAvatar, setEditStaffFullName,
  setIsDirty_Staff_Personal,
  setIsOpenUnsavedModal,
  setIsOpenViewAvatar,
  setStaff_EnumList,
  setStaff_Redirect_URL,
  setStaff_TagList,
  setSelectedRoles
} from '../../redux/slices/staff';
import { cloneDeep, isEqual } from 'lodash';
import {
  getImage,
  saveImage,
  saveOrg
} from '../../redux/slices/organizations';
import { ApiItemTypes, ApiProfileOut3, type ApiImageWrapper, type ApiOrganization } from '@agunity/api-v4';
import AgToast from '../../stories/Components/AgToast';
import AgAvatar from '../../stories/Components/AgAvatar';
import { ValidationListPersonalProps } from './AddStaff/AddStaff';
import { AgColor } from '../../stories/Styles/AgColor';
import AgNoPermission from '../../components/AgNoPermission';
import ProfileConsents from './ProfileConsents';
import ContractForm from '../../components/ContractForm';
import Training from '../../components/Training';
import BindCard from '../MemberInfo/BindCard';
import { Stack } from '@mui/material';

// Wrapper to inject Staff ID as props
export const StaffRouteElementWrapper = () => {
  const { staffId } = useParams();
  if (!staffId) return null;
  return <AgTypography sx={{ padding: '16px 32px', }}>
    <StaffInfo staffId={Number(staffId)} />
  </AgTypography>;
};

const StaffInfo = memo(({ staffId, isProfile }: { staffId: number; isProfile?: boolean }) => {
  const messages = useIntl().formatMessage;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const helpLink = useAppSelector((state) => (state.language.helpLink));
  const user = useAppSelector(state => state.global.users);
  const permissions = (user?.length && user[0].permissions) || [];
  const selectedRoles = useAppSelector(state => state.staff.selectedRoles);
  const orgs = useAppSelector(state => state.full.fullProfile?.organizations);
  const canDoContracts = orgs?.[0]?.license?.canDoContracts;

  const [fullDetails, setFullDetails] = useState<ApiProfileOut3>({});
  const [staffData, setStaffData] = useState<ApiUserInfo[]>([]);
  const [orgData, setOrgData] = useState<ApiOrganizationProfile[]>([]);
  const [originalOrgData, setOriginalOrgData] = useState<ApiOrganizationProfile[]>([]);
  const hasTagList = useAppSelector(state => state.staff.tagList);
  const [tagList, setTagList] = useState<ApiCustomField[]>(hasTagList);
  const hasEnumList = useAppSelector(state => state.staff.enumList);
  const [enumList, setEnumList] = useState<ApiEnumGroup[]>(hasEnumList);
  const isOpenUnsavedModal = useAppSelector(state => state.staff.isOpenUnsavedModal);
  const redirect_URL = useAppSelector(state => state.staff.staff_Redirect_URL);
  const isOpenViewAvatar = useAppSelector(selectIsOpenViewAvatar);
  const [hasPassword, setHasPassword] = useState<boolean>(false);

  // 23 is permission number for editing, 11 is for reading
  const readAccess = isProfile || [MEMBER_READ_PERMISSION, MEMBER_EDIT_PERMISSION].some(num => permissions.includes(num));
  const editAccess = permissions.includes(MEMBER_EDIT_PERMISSION);
  const [personalDetails, setPersonalDetails] = useState<ApiIdentityOut>();
  const [originalPersonalDetails, setOriginalPersonalDetails] = useState<ApiIdentityOut>();
  const originalDateOfBirth = originalPersonalDetails?.dateOfBirth.split('T')[0] ?? '';
  const originalDateOfBirthUnknown = !originalDateOfBirth || (new Date(originalDateOfBirth) >= new Date());
  const dateOfBirth = personalDetails?.dateOfBirth.split('T')[0] ?? '';
  const dateOfBirthUnknown = !dateOfBirth || (new Date(dateOfBirth) >= new Date());
  // if there is no DOB, or DOB is in the future, then age is unknown
  const [ageUnknown, setAgeUnknown] = useState<boolean>(dateOfBirthUnknown);
  const [hasPersonalDetailsError, setHasPersonalDetailsError] = useState<boolean>(false);
  const [hasStaffProfileError, setHasStaffProfileError] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [isToastOpen, setIsToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'neutral'>('success');
  const [saveAndRefresh, setSaveAndRefresh] = useState<boolean>(false);
  const [openDirtyModal, setOpenDirtyModal] = useState<boolean>(false);
  const [profileIndex, setProfileIndex] = useState<number>(0);
  const [showPersonalDetailsError, setShowPersonalDetailsError] = useState<boolean>(false);
  const [showStaffProfileError, setShowStaffProfileError] = useState<boolean>(false);
  const [isHeaderButtonLoading, setIsHeaderButtonLoading] = useState<boolean>(false);
  const [imageURL, setImageURL] = useState<string | undefined>('');
  const [originalImageURL, setOriginalImageURL] = useState<string | undefined>('');
  const [validationListPersonal, setValidationListPersonal] = useState<ValidationListPersonalProps>(
    {
      firstName: [],
      middleName: [],
      lastName: [],
      dateOfBirth: [],
      province: [],
      notes: [],
      city: [],
      postCode: [],
      gender: [],
      language: [],
      address1: [],
      address2: [],
      address3: [],
      country: [],
      latitude: [],
      longitude: [],
      altitude: [],
      phone: [],
      email: []
    }
  );
  const [errorCaptionList, setErrorCaptionList] = useState<string[]>([]);
  const [valueForSaveBtn ,setValueForSaveBtn] = useState<number>(0);
  const [isPersonalDetailsDirty, setIsPersonalDetailsDirty] = useState<boolean>(false);
  const [isOrgDataDirty, setIsOrgDataDirty] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isHeaderButtonDisabled, setIsHeaderButtonDisabled] = useState<boolean>(true);
  const [originalRoles, setOriginalRoles] = useState<number[]>([]);
  
  const fetchData = useCallback(async () => {
    if (!staffId) return;
    try {
      setIsHeaderButtonDisabled(true);
      const getStaffProfileData = await dispatch(getMemberProfile(Number(staffId)));
      setFullDetails(getStaffProfileData.data || {});
      if (getStaffProfileData.data) {
        if (getStaffProfileData.data.account) { // part of test data is dirth, need to remove duplicated fields
          const removeDuplicatedDataArray: ApiItemData[] = [];
          getStaffProfileData.data.account.data?.forEach(data => {
            if (!removeDuplicatedDataArray.find(item => item.key === data.key)) removeDuplicatedDataArray.push(data);
          });
          setPersonalDetails({ ...getStaffProfileData.data.account, data: removeDuplicatedDataArray });
          setOriginalPersonalDetails(cloneDeep({ ...getStaffProfileData.data.account, data: removeDuplicatedDataArray }));
        }
        setHasPassword(getStaffProfileData.data.account?.hasPassword || false);
      }
      if (getStaffProfileData.data?.profiles?.length) {
        setStaffData(getStaffProfileData.data.profiles || []);
        const roleIds = getStaffProfileData.data.profiles?.[0]?.roles?.map(x => Number(x?.id)) || [];
        setOriginalRoles(roleIds);
        dispatch(setSelectedRoles(roleIds));
      }
      if (getStaffProfileData.data?.organizations?.length) {
        setOrgData(getStaffProfileData.data.organizations || []);
        setOriginalOrgData(getStaffProfileData.data.organizations || []);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [dispatch, staffId]);

  useEffect(()=>{
    const isPersonalDetailsDirtyTemp = !isEqual(personalDetails, originalPersonalDetails) || !isEqual(ageUnknown, originalDateOfBirthUnknown) || !isEqual(imageURL, originalImageURL);
    setIsPersonalDetailsDirty(isPersonalDetailsDirtyTemp);
    const isOrgDataDirtyTemp = !isEqual(orgData, originalOrgData) || !isEqual(selectedRoles, originalRoles);
    setIsOrgDataDirty(isOrgDataDirtyTemp);
    setIsDirty(isPersonalDetailsDirtyTemp || isOrgDataDirtyTemp);
    if (isPersonalDetailsDirtyTemp || isOrgDataDirtyTemp){
      dispatch(setIsDirty_Staff_Personal(true));
    } else {
      dispatch(setIsDirty_Staff_Personal(false));
    }
  },[ageUnknown, dispatch, orgData, originalDateOfBirthUnknown, originalOrgData, originalPersonalDetails, originalRoles, personalDetails, selectedRoles, imageURL, originalImageURL]);

  useEffect(()=>{
    // when valueForSaveBtn is 1, it's the yield tab, and save api is different
    const isHeaderBtnDisabled = !editAccess ||
    (selectedCard === 0 && !isPersonalDetailsDirty) ||
    (selectedCard !== 0 && valueForSaveBtn === 0 && !isOrgDataDirty) ||
    (valueForSaveBtn === 1 && selectedCard === 1);
    // (valueForSaveBtn === 1 && (!permissions.includes(60) || !enableYieldSaveBtn)) // todo temp disabled, to be developed
    setIsHeaderButtonDisabled(isHeaderBtnDisabled);
  },[editAccess, isOrgDataDirty, isPersonalDetailsDirty, selectedCard, valueForSaveBtn]);

  const fullName =
    (personalDetails?.firstName ?? '') +
    (personalDetails?.middleName ? ' ' + personalDetails.middleName : '') +
    (personalDetails?.familyName ? ' ' + personalDetails.familyName : '');

  const maxStringAccountProfile = 12;
  const formatForProfileName = (s: string | undefined): string | undefined => {
    return s ? s.length > maxStringAccountProfile
      ? `${s.substring(0, maxStringAccountProfile)}...` : s : undefined;
  };
  const fullNameAccountProfile =
    (formatForProfileName(personalDetails?.firstName) ?? '') +
    (personalDetails?.middleName ? ' ' + formatForProfileName(personalDetails.middleName) : '') +
    (personalDetails?.familyName ? ' ' + formatForProfileName(personalDetails.familyName) : '');
  // for breadcrumb to use the name as header
  dispatch(setEditStaffFullName(fullNameAccountProfile));

  useEffect(() => {
    if (!personalDetails) return;
    if (personalDetails?.imageId !== 0 && imageURL === '') {
      getProfileImage(Number(personalDetails?.imageId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetails]);

  useEffect(()=>{
    const hasErrors = () => {
      let errorList: string[] = [];
      if (!validationListPersonal) return;
      for (const key in validationListPersonal) {
        //@ts-ignore
        validationListPersonal[key].forEach(item => {
          if (!(key.toUpperCase() === 'DATEOFBIRTH' && ageUnknown)){// if age is unknown, then age error caption will be hidden
            errorList.push(item);
          }
        });
      }
      setErrorCaptionList(errorList);
      if (errorList.length) {
        setHasPersonalDetailsError(true);
      } else {
        setHasPersonalDetailsError(false);
      }
    };
    hasErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[ageUnknown, validationListPersonal]);

  useEffect(() => {
    setAgeUnknown(dateOfBirthUnknown);
  }, [dateOfBirthUnknown, personalDetails?.email, personalDetails?.hasPassword]);

  useEffect(() => {
    const fetchTagList = async () => {
      // if (hasTagList?.length || !staffFuncAndRolesId?.functionId) return;
      try {
        const data = { itemType: [CustomFieldItemType.Organization, CustomFieldItemType.Identity, CustomFieldItemType.User], functionId: orgData?.[0]?.function };
        const fetch = await dispatch(getCustomFieldByTagAndFunction(data));
        if (fetch.data) {
          setTagList(fetch.data);
          dispatch(setStaff_TagList(fetch.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    const fetchCustomDropdownOptions = async () => {
      if (hasEnumList?.length) return;
      try {
        const getStaffProfileData = await dispatch(getEnumList());
        if (getStaffProfileData.data) {
          setEnumList(getStaffProfileData.data);
          dispatch(setStaff_EnumList(getStaffProfileData.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetchCustomDropdownOptions();
    fetchData();
    fetchTagList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, staffId, saveAndRefresh]);

  const resetPersonalDetailsToOriginal = () => {
    setPersonalDetails(originalPersonalDetails);
  };

  const getProfileImage = async (imageId: number) => {
    try {
      const response = await dispatch(getImage(ACCOUNT_TYPE_ID, imageId));
      setOriginalImageURL(response?.data?.includes('data:image') ? response.data : 'data:image/jpeg;base64,' + response.data);
      setImageURL(response?.data?.includes('data:image') ? response.data : 'data:image/jpeg;base64,' + response.data);
    } catch (error) {
      setImageURL('');
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const onImageUpdate = (base64?: string) => {
    setImageURL(base64 ?? '');
  };

  const resetStaffDetailsToOriginal = () => {
    setOrgData(originalOrgData);
    dispatch((setSelectedRoles(originalRoles)));
  };

  const handleSetOrgData = (index: number, data: ApiItemData[]) => {
    const updatedOrgData = cloneDeep(orgData);
    updatedOrgData[index].data = data;
    setOrgData(updatedOrgData);
  };

  const handleOrgDataClientId = (index: number, clientId: string) => {
    const updatedOrgData = cloneDeep(orgData);
    updatedOrgData[index].clientId = clientId;
    setOrgData(updatedOrgData);
  };

  const handleSaveAccountInfoChange = async (personalDetails: ApiIdentity) => {
    setIsHeaderButtonLoading(true);
    /** Save image */
    if (!isEqual(imageURL, originalImageURL)) {
      try {
        const payload = {
          itemId: personalDetails?.id,
          typeId: ACCOUNT_TYPE_ID,
          base64: imageURL,
        };

        const response = await dispatch(saveImage(payload as ApiImageWrapper));

        if (response.status === SUCCESS_STATUS) {
          setToastSeverity('success');
          setToastMessage(messages({ id: 'MEMBER_SAVE_SUCCESS_ACCOUNT' }));
          setOriginalImageURL(imageURL);
          setIsToastOpen(true);
        }
      } catch (err) {
      // eslint-disable-next-line no-console
        console.error(err);
        setIsHeaderButtonLoading(false);
        setToastSeverity('error');
        setToastMessage(messages({ id: 'ERROR_MESSAGE' }));
        setIsToastOpen(true);
        return;
      }
    }
    /** Update personal details */
    if (!isEqual(personalDetails, originalPersonalDetails)) {
      if (hasPersonalDetailsError || errorCaptionList.length) {
      // const message = errorCaptionList.join(' '); // if there is an error, show general error message, instead of specific ones
        setShowPersonalDetailsError(true);
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_EDIT_SAVE_ERROR_MESSAGE' }));
        // toast with close icon will not be temporary
        // setTimeout(() => {
        //   setIsToastOpen(false);
        // }, 6000);
        return;
      }
      try {
        setIsHeaderButtonLoading(true);
        await dispatch(updateUser({
          identity: {
            ...personalDetails,
            //@ts-ignore
            data: personalDetails.data?.filter(item => item?.fieldType !== CustomFieldColumnType.Header),
            dateOfBirth: ageUnknown ? '3000-01-01T00:00:00' : personalDetails.dateOfBirth
          }
        }));
        setToastSeverity('success');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_SAVE_SUCCESS_ACCOUNT' }));
        setShowPersonalDetailsError(false);
        dispatch(setIsDirty_Staff_Personal(false));
        setSaveAndRefresh(prevState => !prevState);
      } catch (e) {
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_SAVE_FAIL_ACCOUNT' }));
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        // toast with close icon will not be temporary
        // setTimeout(() => {
        //   setIsToastOpen(false);
        // }, 6000);
      }
    }
    
    setIsHeaderButtonLoading(false);
  };

  const handleSaveOrgInfoChange = async (id: number, orgData: ApiOrganization) => {
    if (hasStaffProfileError) {
      setShowStaffProfileError(true);
      setToastSeverity('error');
      setIsToastOpen(true);
      setToastMessage(messages({ id: 'MEMBER_MISSING_FIELD_TOAST' }));
      // toast with close icon will not be temporary
      // setTimeout(() => {
      //   setIsToastOpen(false);
      // }, 6000);
      return;
    }

    if (!isEqual(selectedRoles, originalRoles)) {
      if (personalDetails) {
        const payload = {
          users: [{
            id: staffData?.[0]?.id,
            roles: selectedRoles ?? staffData?.[0]?.roles,
          }]
        };
  
        try {
          setIsHeaderButtonLoading(true);
          const response = await dispatch(updateIdentity(payload));
          if (response.status === SUCCESS_STATUS) {
            setToastSeverity('success');
            setToastMessage(messages({ id: 'STAFF_PROFILE_SAVE_SUCCESS' }));
            setIsToastOpen(true);
            setSaveAndRefresh(prevState => !prevState);
          }
        } catch (error) {
          setToastSeverity('error');
          setToastMessage(messages({ id: 'STAFF_PROFILE_SAVE_FAIL' }));
          setIsToastOpen(true);
          // eslint-disable-next-line no-console
          console.error(error);
        }
        finally {
          setIsHeaderButtonLoading(false);
        }
      }
    }

    if (!isEqual(orgData, originalOrgData?.[0])) {
      try {
        setIsHeaderButtonLoading(true);
        await dispatch(saveOrg(id, orgData));
        setToastSeverity('success');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_SAVE_SUCCESS_MEMBER' }));
        setShowStaffProfileError(false);
        dispatch(setIsDirty_Staff_Personal(false));
        setSaveAndRefresh(prevState => !prevState);
      } catch (e) {
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_SAVE_FAIL_MEMBER' }));
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setIsHeaderButtonLoading(false);
        // toast with close icon will not be temporary
        // setTimeout(() => {
        //   setIsToastOpen(false);
        // }, 6000);
      }
    }
  };

  const handleHeaderButtonOnClick = async () => {
    if (!personalDetails) return;
    if (selectedCard === 0) {
      await handleSaveAccountInfoChange(personalDetails);
    } else {
      const orgId = orgData[selectedCard - 1].id;
      if (orgId) {
        await handleSaveOrgInfoChange(orgId, orgData[selectedCard - 1] as ApiOrganization);
      }
    }
  };

  const viewAvatarModal = (
    <AgModal
      closeIcon
      open={isOpenViewAvatar}
      setOpen={() => {
        dispatch(setIsOpenViewAvatar(false));
      }}
      title={messages({ id: 'MEMBER_AVATAR_POPUP_TITLE' })}
      body={
        <AgTypography>
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px' }}
            message={fullName}
          />
          <AgTypography style={{ marginTop: '15px', textAlign: 'center' }}>
            {imageURL ?
              <img
                src={imageURL}
                alt={messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' })}
                style={{ width: '508px', height: '466px', objectFit: 'contain' }}
              /> :
              <AgAvatar
                src={defaultAvatar} alt={messages({ id: 'AVATAR_ALT' })}
                style={{ width: '508px', height: '466px' }}
              />}
          </AgTypography>
        </AgTypography>
      }
    />
  );
  const profileUnsavedModal = ( // because this modal would redirect in-between tabs, instead of navigating to other pages, we need it separately
    openDirtyModal &&
    <AgModal
      closeIcon
      open={openDirtyModal}
      setOpen={() => {
        dispatch(setIsOpenUnsavedModal(false));
      }}
      title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
      body={
        <AgTypography
          contentStyle={'AP.Paragraph.Large.Regular'}
          style={{ marginTop: '16px', marginBottom: '80px' }}
          message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
        />
      }
      footer={
        <>
          <AgButton
            label={messages({ id: 'CANCEL' })}
            variant='outlined'
            style={{ marginRight: '32px' }}
            onClick={() => {
              setOpenDirtyModal && setOpenDirtyModal(false);
            }}
          />
          <AgButton
            label={messages({ id: 'PROCEED' })}
            onClick={() => {
              dispatch(setIsDirty_Staff_Personal(false));
              setShowPersonalDetailsError(false);
              setShowStaffProfileError(false);
              setOpenDirtyModal && setOpenDirtyModal(false);
              if (profileIndex) {
                resetPersonalDetailsToOriginal && resetPersonalDetailsToOriginal();
              } else {
                resetStaffDetailsToOriginal && resetStaffDetailsToOriginal();
              }
              setSelectedCard && setSelectedCard(profileIndex ?? 0);
            }}
          />
        </>
      }
      footerSX={{
        boxSizing: 'border-box'
      }}
    />
  );

  const tabsData: AgTabsDataProps[][] = [ // if there are multiple staff profiles, the sequence of staff data and tabs data should be the same
    [
      {
        label: messages({ id: 'PERSONALDETAILS' }),
        children: <PersonalDetails
          identity={personalDetails}
          imageURL={imageURL}
          onImageUpdate={onImageUpdate}
          setPersonalDetails={setPersonalDetails}
          editAccess={editAccess}
          ageUnknown={ageUnknown}
          setAgeUnknown={setAgeUnknown}
          setHasPersonalDetailsError={setHasPersonalDetailsError}
          showPersonalDetailsError={showPersonalDetailsError}
          setValidationListPersonal={setValidationListPersonal}
          validationListPersonal={validationListPersonal}
        />
      },
      {
        label: messages({ id: 'Settings' }),
        children: <Settings
          email={personalDetails?.email || ''}
          setPersonalDetails={setPersonalDetails}
          identity={personalDetails}
          setHasPassword={setHasPassword}
          hasPassword={hasPassword}
          editAccess={editAccess && !isProfile}
          setValidationListPersonal={setValidationListPersonal}
          validationListPersonal={validationListPersonal}
          showPersonalDetailsError={showPersonalDetailsError}
        />
      }
    ].concat(canDoContracts ? [{
      label: messages({ id: 'CONTRACTS' }),
      children: (
        <ContractForm
          canEdit={editAccess}
          itemRef={[
            { itemId: Number(staffId), itemType: ApiItemTypes.Account },
          ]} 
        />
      )
    }] : []),
    staffData.map((item, index) => (
      {
        label: messages({ id: 'Profile Details' }),
        children: <ProfileDetails
          key={index}
          editAccess={editAccess}
          data={item}
          tagList={tagList?.filter(tag => { // todo change the numbers into constant, will ask Stefan the name of constant
            return tag.itemType === 1 || tag.itemType === 53;
          })}
          orgData={orgData[index]}
          setOrgData={(data: ApiItemData[]) => {
            handleSetOrgData(index, data);
          }}
          setOrgDataClientId={(clientId: string) => {
            handleOrgDataClientId(index, clientId);
          }}
          identity={personalDetails}
          setHasStaffProfileError={setHasStaffProfileError}
          showStaffProfileError={showStaffProfileError}
          enumList={enumList}
          isProfile={isProfile}
        />
      }
    ))
    .concat(canDoContracts ? [{
      label: messages({ id: 'CONTRACTS' }),
      children: (
        <ContractForm
          canEdit={editAccess}
          itemRef={[{ itemId: Number(staffData[0]?.id), itemType: ApiItemTypes.User }]} 
        />
      )
    }] : [])
    .concat(
      ...(isProfile
        ? [{
          label: messages({ id: 'CONSENTS' }),
          children: <ProfileConsents />,
        }]
        : []
      )).concat({
        label: messages({ id: 'TRAINING' }),
        children: (
          <Training
            canEdit={editAccess}
            userId={Number(staffData[0]?.id)}
          />
        )
      })
  ];

  const infoCardContent = (
    <AgTypography
      style={{
        display: 'grid',
        gap: '16px'
      }}
    >
      <AgTypography contentStyle={'AP.H5.Medium'} message={messages({ id: 'MEMBER_INFO_CARD' })} />
      <AgTypography contentStyle={'AP.H5.Medium'}>
        {messages({ id: 'LEARN_MORE_IN' }, {
          learn: (
            <AgTypography
              component={'span'}
              contentStyle={'AP.H5.Medium'}
              style={{
                cursor: 'pointer'
              }}
              sx={{
                '&:hover': {
                  color: AgColor.primaryMain
                }
              }}
              onClick={() => { window.open(helpLink, '_blank'); }}
            >
              {<u>{messages({ id: 'HELP_DOC' })}</u>}
            </AgTypography>
          )
        })}
      </AgTypography>
    </AgTypography>
  );

  if (!readAccess) return <AgNoPermission noPadding />;

  return (
    <AgTypography
      style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'start',
        minHeight: '100%'
      }}
    >
      <AgTypography
        style={{
          flex: 1,
          maxWidth: '880px'
        }}
      >
        <AgProfilePage
          headerHasPersonalDetailsError={hasPersonalDetailsError && showPersonalDetailsError}
          headerHasMemberProfileError={hasStaffProfileError && showStaffProfileError}
          headerTabsData={tabsData}
          headerButtonLabel={messages({ id: 'SAVE' })}
          headerButtonDisabled={isHeaderButtonDisabled}
          headerButtonHide={!editAccess}
          headerCardTitle={selectedCard === 0 ? messages({ id: 'PROFILE_ACCOUNT_ID' }) : messages({ id: 'STAFF_ID' })}
          headerCardName={personalDetails?.id ? selectedCard === 0 ? '#a' + personalDetails?.id?.toString() : '#s' + staffData[0]?.id?.toString() : ''}
          headerButtonOnClick={handleHeaderButtonOnClick}
          isHeaderButtonLoading={isHeaderButtonLoading}
          navButtonOnClick={() => {
          }}
          navHideInfoCard={true}
          navAccountImg={imageURL || defaultAvatar}
          navAccountUserType={personalDetails?.purposeType}
          navAccountName={fullNameAccountProfile}
          navAccountID={personalDetails?.id ? '#a' + personalDetails?.id?.toString() : ''}
          navAccountVillage={personalDetails?.address?.city || ''}
          navAccountEmail={personalDetails?.email ?? ''}
          navAccountPhone={personalDetails?.phoneNumber ?? ''}
          navInactive={personalDetails?.activeStatus !== 0} // 0 means active, otherwise, it would be inactive
          navMemberData={staffData || []}
          navOrgData={ orgData || []}
          navMemberProfile={personalDetails?.activeStatus}
          resetPersonalDetailsToOriginal={resetPersonalDetailsToOriginal}
          resetMemberDetailsToOriginal={resetStaffDetailsToOriginal}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          isDirty={isDirty}
          openDirtyModal={openDirtyModal}
          setOpenDirtyModal={setOpenDirtyModal}
          profileIndex={profileIndex}
          setProfileIndex={setProfileIndex}
          profileUnsavedModal={profileUnsavedModal}
          viewAvatarModal={viewAvatarModal}
          style={{
            height: '100%'
          }}
          navHideAddButton //temparary
          setValueForSaveBtn={setValueForSaveBtn}
        />
      </AgTypography>
      <Stack direction='column' gap={1}>
        <AgInfoCard
          title={messages({ id: 'PROFILE_PAGE_NAVI_HEADER1' })}
          closeIcon={false}
          height={'fit-content'}
          children={infoCardContent}
        />
        <BindCard
          isStaff
          isDirty={isDirty}
          details={fullDetails}
          identityId={personalDetails?.id!} 
          isBound={personalDetails?.isBound!}
          permissions={permissions}
          callback={fetchData}
        />
      </Stack>

      <AgModal
        closeIcon
        open={isOpenUnsavedModal}
        setOpen={() => {
          dispatch(setIsOpenUnsavedModal(false));
        }}
        title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
        body={
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px', marginBottom: '80px' }}
            message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
          />
        }
        footer={
          <>
            <AgButton
              label={messages({ id: 'CANCEL' })}
              variant='outlined'
              style={{ marginRight: '32px' }}
              onClick={() => {
                dispatch(setIsOpenUnsavedModal(false));
              }}
            />
            <AgButton
              label={messages({ id: 'PROCEED' })}
              onClick={() => {
                dispatch(setIsOpenUnsavedModal(false));
                setShowPersonalDetailsError(false);
                setShowStaffProfileError(false);
                dispatch(setIsDirty_Staff_Personal(false));
                navigate(redirect_URL, { replace: true });
                dispatch(setStaff_Redirect_URL(''));
                setOriginalRoles([]);
                dispatch(setSelectedRoles([]));
              }}
            />
          </>
        }
        footerSX={{
          boxSizing: 'border-box'
        }}
      />

      {isToastOpen && <AgToast open={isToastOpen} temporary onIconClick={() => {
        setIsToastOpen(false);
      }} severity={toastSeverity} message={toastMessage} />}

    </AgTypography>
  );
});

export default StaffInfo;